import { useState } from "react";
import {
  Box,
  useTheme,
  Card,
  IconButton,
  Chip,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import AppButton from "../buttons/AppButton";
import CustomTypography from "../form/CustomTypography";
import { observer } from "mobx-react";
import { PackProduct } from "../../../../models/Order/OrderProduct";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import defaultImage from "../../../../assets/Boxopen.svg";

interface InvoiceListingProps {
  packs?: PackProduct[];
  padding?: string;
  boxShadow?: string;
  toggleAddProductsPopup: () => void;
  canCreateInvoice?: boolean;
  canDownloadInvoice?: boolean;
  canDeletePack?: boolean;
  handleDeletePack: (id: number) => void;
  handleImageUpload?: (pack: PackProduct) => void;
}

const PackConfigTable: React.FC<InvoiceListingProps> = ({
  packs,
  padding,
  boxShadow,
  toggleAddProductsPopup,
  handleDeletePack,
  canCreateInvoice = true,
  canDeletePack = true,
  handleImageUpload,
}) => {
  const theme = useTheme();
  const [hoverIndex, setHoverIndex] = useState<number | null>(null);

  return (
    <Box sx={{ borderRadius: 2 }}>
      <Card
        sx={{
          p: padding || { xs: 2, md: 3 },
          borderRadius: "12px",
          border: `1px solid ${theme.palette.customColors.lightGray}`,
          boxShadow:
            boxShadow ||
            `0 2px 8px ${theme.palette.customColors.shadowLightBorder}`,
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 3,
          }}
        >
          <CustomTypography variant="heading4" fontWeight={700}>
            Pack Configurations
          </CustomTypography>
          {canCreateInvoice && (
            <AppButton
              label="Add"
              startIcon={<AddIcon />}
              onClick={toggleAddProductsPopup}
              customStyles={{
                backgroundColor: "transparent",
                padding: 1,
                margin: 0,
                fontSize: "14px",
                color: theme.palette.customColors.pinkDark,
                "&:hover": {
                  backgroundColor: "transparent",
                  color: theme.palette.customColors.pinkDark,
                  boxShadow: "none",
                },
              }}
            />
          )}
        </Box>

        {!packs || packs.length === 0 ? (
          <Box sx={{ textAlign: "center", py: 3 }}>
            <CustomTypography
              variant="body2"
              fontWeight={700}
              color={theme.palette.customColors.greyTextColor}
            >
              NO PACK FOUND!
            </CustomTypography>
          </Box>
        ) : (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
            {packs.map((pack, index) => (
              <Box
                key={`${pack.id}-${index}`}
                sx={{
                  border: "1px solid #E2E8F0",
                  borderLeft: `3px solid ${theme.palette.primary.main}`,
                  borderRadius: "10px",
                  overflow: "hidden",
                  backgroundColor: "#fff",
                }}
              >
                {/* Header row */}
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 2,
                    px: 2,
                    py: 1.25,
                    flexWrap: "wrap",
                  }}
                >
                  {/* Pack image with hover overlay */}
                  <Box
                    sx={{
                      position: "relative",
                      background: "#F1F5F9",
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      minHeight: 44,
                      maxHeight: 44,
                      minWidth: 44,
                      maxWidth: 44,
                      borderRadius: "6px",
                      flexShrink: 0,
                      cursor: "pointer",
                    }}
                    onMouseEnter={() => setHoverIndex(index)}
                    onMouseLeave={() => setHoverIndex(null)}
                  >
                    <img
                      style={{
                        maxHeight: 32,
                        minHeight: 32,
                        maxWidth: 32,
                        minWidth: 32,
                        objectFit: "contain",
                      }}
                      src={pack.image_url || defaultImage}
                      alt={pack.label}
                    />
                    {hoverIndex === index && (
                      <Box
                        sx={{
                          position: "absolute",
                          top: 0,
                          left: 0,
                          width: "100%",
                          height: "100%",
                          backgroundColor: "#00000030",
                          display: "flex",
                          justifyContent: "center",
                          alignItems: "center",
                          borderRadius: "6px",
                        }}
                      >
                        <VisibilityOutlinedIcon
                          sx={{
                            color: "#fff",
                            cursor: "pointer",
                            backgroundColor: "#00000090",
                            borderRadius: "50%",
                            padding: "3px",
                            height: 18,
                            width: 18,
                          }}
                          onClick={() => handleImageUpload!(pack)}
                        />
                      </Box>
                    )}
                  </Box>

                  <Box sx={{ flex: 1, minWidth: 100 }}>
                    <CustomTypography variant="body2" fontWeight={700}>
                      {pack.label}
                    </CustomTypography>
                    {pack.creator_name && (
                      <CustomTypography variant="body3" color="#64748B">
                        by {pack.creator_name}
                      </CustomTypography>
                    )}
                  </Box>

                  <Chip
                    label={pack.active ? "Active" : "Inactive"}
                    size="small"
                    sx={{
                      fontSize: "0.72rem",
                      fontWeight: 600,
                      height: 22,
                      backgroundColor: pack.active ? "#DCFCE7" : "#F1F5F9",
                      color: pack.active ? "#16A34A" : "#64748B",
                      border: `1px solid ${pack.active ? "#BBF7D0" : "#E2E8F0"}`,
                    }}
                  />

                  {pack.id !== undefined && canDeletePack && (
                    <IconButton
                      size="small"
                      sx={{
                        backgroundColor: theme.palette.customColors.pinkLight,
                        width: 28,
                        height: 28,
                        "&:hover": {
                          backgroundColor:
                            theme.palette.customColors.pinkLight + "cc",
                        },
                      }}
                      onClick={() => handleDeletePack(pack.id!)}
                    >
                      <DeleteIcon
                        sx={{
                          color: theme.palette.customColors.pinkDark,
                          width: 16,
                          height: 16,
                        }}
                      />
                    </IconButton>
                  )}
                </Box>

                {/* Metrics strip */}
                <Box
                  sx={{
                    px: 2,
                    py: 1,
                    backgroundColor: "#FAFAFA",
                    borderTop: "1px solid #F1F5F9",
                    display: "flex",
                    flexWrap: "wrap",
                    gap: 3,
                  }}
                >
                  {[
                    {
                      label: "Products",
                      value:
                        pack.products
                          ?.map((p) => p.product_name)
                          .join(", ") || "--",
                    },
                    { label: "Expected Qty", value: pack.expected_quantity },
                    {
                      label: "Created",
                      value: pack.created_date
                        ? new Date(pack.created_date).toLocaleDateString()
                        : "-",
                    },
                    {
                      label: "Modified",
                      value: pack.modified_date
                        ? new Date(pack.modified_date).toLocaleDateString()
                        : "-",
                    },
                  ].map((m) => (
                    <Box
                      key={m.label}
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 0.25,
                        minWidth: 68,
                      }}
                    >
                      <Box
                        component="span"
                        sx={{
                          fontSize: "0.63rem",
                          color: "#94A3B8",
                          fontWeight: 700,
                          textTransform: "uppercase",
                          letterSpacing: "0.04em",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {m.label}
                      </Box>
                      <Box
                        component="span"
                        sx={{
                          fontSize: "0.82rem",
                          fontWeight: 600,
                          color: "#334155",
                        }}
                      >
                        {m.value}
                      </Box>
                    </Box>
                  ))}
                </Box>
              </Box>
            ))}
          </Box>
        )}
      </Card>
    </Box>
  );
};

export default observer(PackConfigTable);
